import { useCallback, useEffect, useRef } from 'react';

const TAB_INSTANCE_ID_KEY = 'current_page_tab_id_';
const ACTIVE_TABS_REGISTRY_KEY = 'active_page_tabs_registry_';
const HEARTBEAT_INTERVAL_MS = 3000;
const EXPIRATION_THRESHOLD_MS = 5000;

function generateUniqueId(): string {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
}

export function useMultiTabDetector(onDuplicateTabDetected: () => void, id?: string): void {
  const currentTabIdRef = useRef(sessionStorage.getItem(TAB_INSTANCE_ID_KEY) || generateUniqueId());

  const memoizedOnDuplicateTabDetected = useCallback(onDuplicateTabDetected, [
    onDuplicateTabDetected,
  ]);

  useEffect(() => {
    if (!sessionStorage.getItem(TAB_INSTANCE_ID_KEY + id ?? '')) {
      sessionStorage.setItem(TAB_INSTANCE_ID_KEY + id ?? '', currentTabIdRef.current);
    }

    const checkOtherTabs = (): boolean => {
      const now = Date.now();
      let activeTabs: Record<string, number> = {};

      try {
        const storedRegistry = localStorage.getItem(ACTIVE_TABS_REGISTRY_KEY + id ?? '');
        if (storedRegistry) {
          activeTabs = JSON.parse(storedRegistry);
        }
      } catch (error) {
        console.error('error parsing localstorage', error);
        activeTabs = {};
      }

      let otherTabsFound = false;
      // eslint-disable-next-line no-restricted-syntax
      for (const tabId in activeTabs) {
        if (tabId === currentTabIdRef.current) {
          // eslint-disable-next-line no-continue
          continue; // Ignore the current tab
        }

        const lastActiveTime = activeTabs[tabId];
        if (now - lastActiveTime < EXPIRATION_THRESHOLD_MS) {
          otherTabsFound = true;
          break; // other active tab
        } else {
          // expired tab
          delete activeTabs[tabId];
        }
      }
      // Store all states in LocalStorage
      localStorage.setItem(ACTIVE_TABS_REGISTRY_KEY + id ?? '', JSON.stringify(activeTabs));

      return otherTabsFound;
    };

    /**
     * update timestamp
     */
    const updateOwnHeartbeat = () => {
      const now = Date.now();
      let activeTabs: Record<string, number> = {};

      try {
        const storedRegistry = localStorage.getItem(ACTIVE_TABS_REGISTRY_KEY + id ?? '');
        if (storedRegistry) {
          activeTabs = JSON.parse(storedRegistry);
        }
      } catch (error) {
        console.error('Erreur de parsing lors de la mise à jour du heartbeat:', error);
        activeTabs = {};
      }

      activeTabs[currentTabIdRef.current] = now;
      localStorage.setItem(ACTIVE_TABS_REGISTRY_KEY + id ?? '', JSON.stringify(activeTabs));
    };

    const areOtherTabsAlreadyOpen = checkOtherTabs();
    if (areOtherTabsAlreadyOpen) {
      memoizedOnDuplicateTabDetected();
    } else {
      console.log('First time tab.');
    }

    // --- Heartbeat ---
    updateOwnHeartbeat(); // first heartBeat for the first tab
    const intervalId = setInterval(updateOwnHeartbeat, HEARTBEAT_INTERVAL_MS); // Heartbeat régulier

    // --- cleanup ---
    return () => {
      clearInterval(intervalId); // stop heartbeat

      let activeTabs: Record<string, number> = {};
      try {
        const storedRegistry = localStorage.getItem(ACTIVE_TABS_REGISTRY_KEY + id ?? '');
        if (storedRegistry) {
          activeTabs = JSON.parse(storedRegistry);
        }
      } catch (error) {
        console.error('Erreur parsing in localStorage:', error);
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
      delete activeTabs[currentTabIdRef.current];
      localStorage.setItem(ACTIVE_TABS_REGISTRY_KEY + id ?? '', JSON.stringify(activeTabs));
    };
  }, [memoizedOnDuplicateTabDetected, id]);
}
